import React, { useEffect, useState } from "react";
import { useTopic } from "@/hooks/useTopic";
import { Button, Flex, IconName, Text, usePortalContainer } from "@theorgchartdev/storybook";
import { autoUpdate, useDismiss, useFloating, useInteractions } from "@floating-ui/react";
import { createPortal } from "react-dom";
import { appstr } from "@coreapp/utils/appstr";
import { QAlert } from "@coreapp/CoreApp/QAlert";
import * as StateLib from "@/Utils/StateLib";
import {
  gSupportPortal,
  gHelpWiki,
  gSupportMsgKey,
  gUseGenericSupportContact
} from "@coreapp/whitelabel/brand";
import { useNativeGuides } from "./NativeGuidesProvider";

// TODO: If reimplementing this feature, ensure an appropriate anchor element exists in the DOM for HELP_ANCHOR to query.
const HELP_ANCHOR = '[data-testid="header-help-btn"]';

const ONBOARDING_GUIDE_IDS = ["feature-tour"];

const NativeResourceCenter: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { retriggerGuide } = useNativeGuides();
  useTopic("nativeResourceCenter:open", () => {
    setIsOpen(true);
  });
  const portalContainer = usePortalContainer();
  const { refs, floatingStyles, context } = useFloating({
    strategy: "fixed",
    placement: "bottom-end",
    whileElementsMounted: autoUpdate,
    open: isOpen,
    onOpenChange: setIsOpen
  });
  const dismiss = useDismiss(context);
  const { getFloatingProps } = useInteractions([dismiss]);

  useEffect(() => {
    const el = document.querySelector(HELP_ANCHOR);
    if (el) {
      refs.setReference(el);
      return;
    }

    // Anchor isn't mounted yet — wait for it, then disconnect.
    const observer = new MutationObserver(() => {
      const found = document.querySelector(HELP_ANCHOR);
      if (found) {
        refs.setReference(found);
        observer.disconnect();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- refs is stable

  if (!isOpen) return null;

  const handleSupportPortalClick = () => {
    if (gUseGenericSupportContact) {
      const alert = new QAlert();
      alert.InitAlert(appstr("Support portal"), appstr(gSupportMsgKey), null, StateLib.GetWSMgr());
      return;
    }
    window.open(gSupportPortal, "_blank", "noopener,noreferrer");
  };

  const resourceCenter = (
    <Flex
      direction="column"
      ref={refs.setFloating}
      style={floatingStyles}
      className="oc-resource-center"
      {...getFloatingProps()}
    >
      <Flex
        padding="24px"
        justifyContent="space-between"
        alignItems="center"
        className="oc-resource-center-header"
      >
        <Text modifier="bold">{appstr("Resource center")}</Text>
        <Button
          onClick={() => setIsOpen(false)}
          iconProps={{
            name: "xmark"
          }}
          modifier="tertiary"
          iconOnly
        />
      </Flex>
      <Flex direction="column" gap="12px" padding="8px">
        <ResourceCenterButton
          onClick={handleSupportPortalClick}
          header={appstr("Support portal")}
          description={appstr("Submit a support ticket")}
          data-testid="resource-center-support-portal"
          icon="angle-right"
        />
        <ResourceCenterButton
          onClick={() => window.open(gHelpWiki, "_blank", "noopener,noreferrer")}
          header={appstr("Help documentation")}
          description={appstr("Learn about specific topics")}
          data-testid="resource-center-help-docs"
          icon="angle-right"
        />
        <ResourceCenterButton
          onClick={() => {
            retriggerGuide(...ONBOARDING_GUIDE_IDS);
            setIsOpen(false);
          }}
          header={appstr("Onboarding")}
          description={appstr("Take a tour of our core features")}
          data-testid="resource-center-onboarding-guides"
        />
      </Flex>
    </Flex>
  );

  return createPortal(resourceCenter, portalContainer ?? document.body);
};

export default NativeResourceCenter;

interface ResourceCenterButtonProps {
  onClick: () => void;
  header: string;
  description: string;
  icon?: IconName;
  ["data-testid"]?: string;
}

const ResourceCenterButton = ({
  onClick,
  "data-testid": dataTestId,
  header,
  description,
  icon
}: ResourceCenterButtonProps) => {
  return (
    <Button
      onClick={onClick}
      data-testid={dataTestId}
      size="large"
      modifier="tertiary"
      iconProps={icon ? { name: icon } : undefined}
      iconAlignEnd
      className="oc-resource-center-button"
    >
      <Flex direction="column" gap="4px">
        <Text modifier="medium" size="small">
          {header}
        </Text>
        <Text size="xsmall">{description}</Text>
      </Flex>
    </Button>
  );
};
